import { useState, useEffect, useCallback } from "react";
import { httpClient } from "@/lib/http-client";

export interface AuthenticatedObjectUrl {
  /** The blob: URL, once it has arrived. */
  objectUrl: string | null;
  /** The fetch finished and failed. Distinct from "still loading" (both null). */
  failed: boolean;
  /** Ask again — for a "Try again" next to the error. */
  retry: () => void;
}

/**
 * Fetches a file from an authenticated API endpoint and returns an object URL,
 * saying whether it is still coming or has failed.
 *
 * Use this wherever an element loads a URL itself — `<img>`, `<iframe>`,
 * `<audio>` — because none of them send an Authorization header. Each blob:
 * URL also needs its own CSP directive; `default-src` does not cover blob:,
 * and both blocks in server/config/middleware.ts have to say so.
 *
 * The failure flag matters: a caller that only has "no URL yet" renders a
 * spinner for ever when the file 404s, which looks like a slow network and
 * hides the actual error.
 */
export function useAuthenticatedObjectUrlState(url: string | null): AuthenticatedObjectUrl {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    setFailed(false);
    if (!url) {
      setObjectUrl(null);
      return;
    }

    let revoked = false;
    let currentUrl: string | null = null;

    httpClient
      .downloadBlob(url)
      .then((blob) => {
        if (revoked) return;
        currentUrl = URL.createObjectURL(blob);
        setObjectUrl(currentUrl);
      })
      .catch(() => {
        if (revoked) return;
        setObjectUrl(null);
        setFailed(true);
      });

    return () => {
      revoked = true;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
    };
  }, [url, attempt]);

  return { objectUrl, failed, retry };
}

/** Just the URL, for the many callers that only show it when it arrives. */
export function useAuthenticatedObjectUrl(url: string | null): string | null {
  return useAuthenticatedObjectUrlState(url).objectUrl;
}

/** The same thing, named for its oldest and commonest use. */
export const useAuthenticatedImage = useAuthenticatedObjectUrl;
